import React, { useContext, useState } from 'react';
import { API_BASE } from '../utils/apiBase';
import { CartContext, EXTRA_SCRATCH_PRICE_CENTS } from '../context/CartContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { resolveMediaUrl, getDemoPath } from '../utils/media';

const Cart = () => {
  const { cart, removeFromCart, updateCartItemQuantity, updateCartItemExtras, promoCode, setPromoCode, discount, setDiscount } = useContext(CartContext);
  const TVA_RATE = 0.2; 

  const [promoInput, setPromoInput] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [loading, setLoading] = useState(false); 

  const isMobile = useMediaQuery('(max-width:768px)'); // Vérifier si l'écran est mobile

  // product.price est un montant TTC en centimes : la TVA est incluse, pas ajoutée
  const totalTTCBrut = cart.reduce((acc, product) => acc + (product.price / 100) * product.quantity, 0);
  const totalHT = totalTTCBrut / (1 + TVA_RATE);
  const totalTVA = totalTTCBrut - totalHT;
  const totalTTC = totalTTCBrut - (typeof discount === 'number' ? discount : 0);

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    updateCartItemQuantity(index, newQuantity);
  };

  const handlePromoCodeApply = async () => {
    setLoading(true);
    setPromoError(null);

    try {
      const response = await axios.get(`${API_BASE}/apiEtsy.php`, {
        params: {
          cleApi: "34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75",
          service: 'checkPromoCode',
          promoCode: promoInput,
        },
      });

      const { discount_type, discount_value } = response.data;

      setPromoCode(promoInput);
      if (discount_type === 'percentage') {
        setDiscount(totalTTCBrut * (discount_value / 100));
      } else if (discount_type === 'fixed') {
        setDiscount(parseFloat(discount_value));
      }

      setPromoApplied(true);
    } catch (error) {
      setPromoError('Code promo invalide ou expiré.');
      setDiscount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setDiscount(0);   
    setPromoApplied(false); 
    setPromoError(null);
  };

  if (cart.length === 0) {
    return <Typography variant="h5" align="center" sx={{ mt: 5 }}>Votre panier est vide.</Typography>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h1" sx={{ marginBottom: 3, color: '#c48f8b' }}>Votre panier</Typography>
      
      <Grid container spacing={3}>
        {cart.map((product, index) => (
          <Grid 
            item xs={12} 
            key={index} 
            sx={{ 
              padding: 2,
              borderBottom: '1px solid #ddd'
            }}
          >
            {/* Ligne 1 : Image + Quantité + Supprimer */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'row', 
              alignItems: 'center', 
              justifyContent: 'space-between' 
            }}>
              {/* Image */}
              <Box sx={{ width: '80px', textAlign: 'center' }}>
                <img
                  src={resolveMediaUrl(
                    // visuelSku présent (config_carte) → toujours la démo, jamais le fond brut.
                    // Sans visuelSku, le produit n'a aucun visuel rattaché : selectedVisual
                    // pointe alors sur featured_image, une image de démo déjà prête (pas un fond).
                    // Produit « à type » (faire-part animé, galerie) : l'image de sa fiche.
                    product.productType && product.featuredImage
                      ? product.featuredImage
                      : product.visuelSku
                      ? getDemoPath(product.visuelSku)
                      : (product.selectedVisual || product.featuredImage || '')
                  )}
                  alt={product.name}
                  style={{ width: '100%' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </Box>

              {/* Quantité */}
              <TextField
                label="Quantité"
                type="number"
                value={product.quantity}
                onChange={(e) => handleQuantityChange(index, parseInt(e.target.value))}
                sx={{ width: '80px', marginX: 2 }}
                inputProps={{ min: 1 }}
              />

              {/* Supprimer */}
              <Button
                variant="contained"
                color="error"
                onClick={() => removeFromCart(index)}
                sx={{
                  width: '100px',
                  textAlign: 'center'
                }}
              >
                Supprimer
              </Button>

              {/* Total de la ligne (prix carte + options) × quantité, bien visible à droite */}
              <Typography
                sx={{
                  marginLeft: 'auto',
                  paddingLeft: 2,
                  fontSize: { xs: '1.3rem', md: '1.6rem' },
                  fontWeight: 700,
                  color: '#8C8BC4',
                  whiteSpace: 'nowrap',
                }}
              >
                {((product.price * product.quantity) / 100).toFixed(2).replace('.', ',')} €
              </Typography>
            </Box>

            {/* Ligne 2 : Détails du produit */}
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="h6">{product.name}</Typography>
              {product.customText && (
                <Typography variant="body2" color="text.secondary">
                  Personnalisation : {product.customText}
                </Typography>
              )}
              {product.customTitre && (
                <Typography variant="body2" color="text.secondary">
                  Titre affiché : {product.customTitre}
                </Typography>
              )}
              {/* Mini-site de mariage (et futurs produits option_schema) : pas de grattage,
                  on résume plutôt les choix faits dans le configurateur (ProductDetail.js). */}
              {product.productType ? (
                <Typography variant="body2" color="text.secondary">
                  {product.productType === 'minisite'
                    ? 'Personnalisation dans notre éditeur en ligne, dont vous recevez le lien par e-mail après l\'achat'
                    : 'Mise en place sous 48 h, par e-mail'}
                </Typography>
              ) : (
                /* Option "personnes supplémentaires" : réglable directement ici, indépendante
                   de la quantité (une carte = 2 grattages inclus, +0,50 € par personne en plus). */
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 1.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ color: '#3a2b2b' }}>
                    Personnes supplémentaires (+{(EXTRA_SCRATCH_PRICE_CENTS / 100).toFixed(2).replace('.', ',')} € chacune) :
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Retirer une personne supplémentaire"
                    onClick={() => updateCartItemExtras(index, (product.extraScratches || 0) - 1)}
                    disabled={!(product.extraScratches > 0)}
                    sx={{ border: '1px solid #e0d5d3' }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ minWidth: 20, textAlign: 'center', fontWeight: 600 }}>
                    {product.extraScratches || 0}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Ajouter une personne supplémentaire"
                    onClick={() => updateCartItemExtras(index, (product.extraScratches || 0) + 1)}
                    sx={{ border: '1px solid #e0d5d3' }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {(product.maxScratchAllowed || 2)} grattages au total · {(product.price / 100).toFixed(2).replace('.', ',')} € la carte
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      
      <Divider sx={{ marginY: 3 }} />

      {/* Code Promo */}
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 2 }}>
        <TextField
          label="Code promo"
          value={promoInput}
          onChange={(e) => setPromoInput(e.target.value)}
          variant="outlined"
          sx={{ marginRight: isMobile ? 0 : 2, marginBottom: isMobile ? '10px' : 0 }}
          disabled={promoApplied}
        />
        <Button
          variant="contained"
          color="secondary"
          onClick={handlePromoCodeApply}
          disabled={promoApplied || loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Appliquer'}
        </Button>
      </Box>

      {promoError && <Alert severity="error">{promoError}</Alert>}

      {discount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2 }}>
          <Typography variant="h6" color="green">
            Réduction : -{discount.toFixed(2)} €
          </Typography>
          <Button
            variant="text"
            color="primary"
            onClick={handleRemovePromo}
            sx={{ marginLeft: 2 }}
          >
            Supprimer le code promo
          </Button>
        </Box>
      )}

      {/* Totaux */}
      <Typography variant="h6">Total HT : {totalHT.toFixed(2)} €</Typography>
      <Typography variant="h6">TVA ({TVA_RATE * 100} %) : {totalTVA.toFixed(2)} €</Typography>
      <Typography variant="h5" sx={{ marginTop: 2 }}>Total TTC : {totalTTC.toFixed(2)} €</Typography>

      <Button variant="contained" color="primary" component={Link} to="/checkout" sx={{ marginTop: 3, width: isMobile ? '100%' : 'auto' }}>
        Procéder au paiement
      </Button>
    </div>
  );
};

export default Cart;
